// 1. React and React-related imports
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// 2. Third-party libraries

// 3. Local components
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  CardFooter 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// 4. Hooks
import { useAuthContext } from '@/features/auth/hooks/useAuthContext';
import { useGuests } from './useGuests';
import { useFeedback } from "@/shared/contexts/FeedbackContext";

// 5. Types
import { Guest } from '@/types/models/schema';
import { ApiResponse } from '@/types/api';

// 6. Utils
import { ROUTES, generateRoute } from '@/constants/routes';

// 7. Icons and assets
import { ArrowLeft, Edit, MapPin, Calendar, Mail, Building, User } from 'lucide-react';

/**
 * Simplified GuestDetail Component
 * 
 * Displays detailed information about a podcast guest
 */
const GuestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast: showToast } = useFeedback();
  const { user, userRole } = useAuthContext();
  const { getGuest } = useGuests();
  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchGuest = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const guestResponse = await getGuest(id);
        
        if (guestResponse.error || !guestResponse.data) {
          throw new Error(guestResponse.error?.message || 'Failed to load guest details');
        }
        
        setGuest(guestResponse.data);
      } catch (error) {
        showToast({
          title: 'Error',
          description: 'Failed to load guest details',
          type: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchGuest();
  }, [id, getGuest, showToast]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!guest) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <h2 className="text-xl font-semibold mb-2">Guest Not Found</h2>
            <p className="text-neutral-500 mb-4">The guest you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate(ROUTES.GUESTS)}>
              Back to Guest List
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(ROUTES.GUESTS)}
          className="flex items-center gap-1"
        >
          <ArrowLeft size={16} />
          <span>Back to Guests</span>
        </Button>
      </div>
      
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 font-heading">{guest.name}</h1>
          <p className="text-neutral-500 mt-1">
            {guest.title || ''} {guest.title && guest.company ? 'at' : ''} {guest.company || ''}
          </p>
        </div>
        
        {userRole === 'admin' && guest.id && (
          <Button 
            onClick={() => navigate(generateRoute(ROUTES.GUEST_EDIT, { id: guest.id || '' }))}
            className="flex items-center gap-2"
          >
            <Edit size={16} />
            <span>Edit Guest</span>
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Guest Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-neutral-500">Name</p>
                <div className="flex items-center gap-2">
                  <User size={16} className="text-neutral-400" />
                  <p className="font-medium">{guest.name}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-neutral-500">Email</p>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-neutral-400" />
                  <p className="font-medium">{guest.email}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-neutral-500">Company</p>
                <div className="flex items-center gap-2">
                  <Building size={16} className="text-neutral-400" />
                  <p className="font-medium">{guest.company || 'N/A'}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-neutral-500">Title</p>
                <p className="font-medium">{guest.title || 'N/A'}</p>
              </div>
              
              {guest.location && (
                <div className="space-y-1 md:col-span-2">
                  <p className="text-sm text-neutral-500">Location</p>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-neutral-400" />
                    <p className="font-medium">{guest.location.name}</p>
                  </div>
                </div>
              )}
            </div>
            
            {guest.bio && (
              <div className="space-y-1 pt-2">
                <p className="text-sm text-neutral-500">Bio</p>
                <p className="text-neutral-700 whitespace-pre-line">{guest.bio}</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Episode Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-neutral-500">Status</p>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  guest.status === 'confirmed' 
                    ? 'bg-green-100 text-green-800' 
                    : guest.status === 'pending' 
                    ? 'bg-yellow-100 text-yellow-800'
                    : guest.status === 'recorded'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {guest.status.charAt(0).toUpperCase() + guest.status.slice(1)}
                </span>
              </div>
            </div>
            
            {/* Use optional chaining for episode_number and episode_date */}
            {guest.episode_number !== undefined && guest.episode_number !== null && (
              <div className="space-y-1">
                <p className="text-sm text-neutral-500">Episode Number</p>
                <p className="font-medium">#{guest.episode_number}</p>
              </div>
            )}
            
            {guest.episode_date && (
              <div className="space-y-1">
                <p className="text-sm text-neutral-500">Recording Date</p>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-neutral-400" />
                  <p className="font-medium">{guest.episode_date}</p>
                </div>
              </div>
            )}
            
            {!guest.episode_number && !guest.episode_date && (
              <div className="py-4 text-center text-neutral-500">
                <p>No episode details available yet</p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            {userRole === 'admin' && guest.status !== 'published' && guest.id && (
              <Button 
                className="w-full"
                variant={guest.status === 'pending' ? 'default' : 'outline'}
                onClick={() => navigate(generateRoute(ROUTES.GUEST_SCHEDULE, { id: guest.id || '' }))}
              >
                {guest.status === 'pending' ? 'Schedule Episode' : 'Update Schedule'}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default GuestDetail;
